<script lang="ts" setup>
export type DataKeys = 'title' | 'hash';
export type DataKeysValues = string | VNuxtLinkTo;
export type PageHeaderMenuItem = Record<DataKeys, DataKeysValues>;
export interface PageHeaderMenuProps {
    data: PageHeaderMenuItem[];
    cta?: PageHeaderCTA;
    button?: {
        label: string;
        link: string;
    };
}

const props = defineProps<PageHeaderMenuProps>()
const { $t } = useNuxtApp()
const activeHash = ref<VNuxtLinkTo>()
const route = useRoute()
const { data: gaEventData, sendEvent } = useTracker()
const { setShow } = usePageHeaderMenu()
const open = ref<boolean>(false)
const menuUlElement = ref<HTMLUListElement>()
const unvisibleListElements = ref<HTMLLIElement[]>([])
const mobileMenu = ref<HTMLDivElement>()
const showPrevButton = ref(false)
const showNextButton = ref(true)

const activeRoute = computed(() => {
    return route.path || ''
})

const dropdownItems = computed(() => {
    const items: PageHeaderMenuItem[] = []
    unvisibleListElements.value.forEach((element) => {
        const item = props.data.find(
            (item) => item.title === element.textContent
        )
        if (item) {
            items.push(item)
        }
    })

    return items
})

const handleClick = (item: PageHeaderMenuItem): void => {
    sendEvent({
        data: {
            ...gaEventData,
            click: item.title,
        },
    })
}

const linkUrl = computed<VNuxtLinkTo>(
    (): VNuxtLinkTo => useCtaLinkUrl(props.cta)
)

const getUvisibleListElement = () => {
    if (!menuUlElement.value) return
    const container = menuUlElement.value // replace with your container selector
    const containerRect = container.getBoundingClientRect()
    const containerWidth = container.clientWidth
    const children = Array.from(container.children) as HTMLLIElement[] // get all direct child elements
    children.forEach((child) => {
        child.style.display = 'block'
    })

    unvisibleListElements.value = []

    // Find elements that are partially or fully outside the visible area
    unvisibleListElements.value = children.filter((child) => {
        const childRect = child.getBoundingClientRect()
        const childLeft = childRect.left - containerRect.left
        const childRight = Math.floor(childLeft + child.clientWidth)
        // Check if the element is partially or fully outside the visible area
        return childRight > containerWidth
    })

    unvisibleListElements.value.forEach((child) => {
        child.style.display = 'none'
    })
}

const intersectionOptions = reactive({
    root: null,
    rootMargin: '-20% 0px -20% 0px',
    threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0],
})

let observer: IntersectionObserver | null = null

const restartObserver = () => {
    if (observer) {
        observer.disconnect()
        observer = null
    }
    observer = new IntersectionObserver((entries) => {
        const viewportHeight = window.innerHeight
        const viewportCenter = viewportHeight / 2

        entries.forEach((entry) => {
            const rect = entry.boundingClientRect
            const isCenterTouching =
                viewportCenter >= rect.top - 50 &&
                viewportCenter <= rect.bottom + 50

            if (entry.isIntersecting && isCenterTouching) {
                const hash = `#${entry.target.id}`
                activeHash.value = hash

                // Scroll mobile menu to show active link without using scrollIntoView
                // which can steal focus (WCAG 2.4.3 compliance)
                const mobileLink = document.querySelector(
                    `.page-header-mobile-menu-item[href="${hash}"]`
                ) as HTMLAnchorElement

                if (mobileLink && mobileMenu.value) {
                    const container = mobileMenu.value
                    const linkRect = mobileLink.getBoundingClientRect()
                    const containerRect = container.getBoundingClientRect()

                    // Calculate scroll position to center the link
                    const linkCenter = linkRect.left + linkRect.width / 2
                    const containerCenter = containerRect.left + containerRect.width / 2
                    const scrollOffset = linkCenter - containerCenter

                    container.scrollBy({
                        left: scrollOffset,
                        behavior: 'smooth',
                    })
                }
            }
        })
    }, intersectionOptions)

    // Повторно додати всі елементи для спостереження
    const sectionsWithAnchor = document.querySelectorAll(
        'section[id], [data-scroll-id]'
    ) as NodeListOf<HTMLElement>

    sectionsWithAnchor.forEach((section) => {
        observer?.observe(section)
    })
}

const handleOrientationChange = () => {
    const orientation = window.screen.orientation.type
    if (orientation.startsWith('landscape')) {
        intersectionOptions.rootMargin = '-50% 0px -0% 0px'
    } else if (orientation.startsWith('portrait')) {
        intersectionOptions.rootMargin = '-20% 0px -20% 0px'
    }
    restartObserver()
}

const handlePrev = () => {
    if (!mobileMenu.value) return

    const scrollAmount = -100
    mobileMenu.value.scrollBy({
        left: scrollAmount,
        behavior: 'smooth',
    })
}

const handleNext = () => {
    if (!mobileMenu.value) return

    const scrollAmount = 100
    mobileMenu.value.scrollBy({
        left: scrollAmount,
        behavior: 'smooth',
    })
}

const checkScrollButtons = () => {
    if (!mobileMenu.value) return

    const tolerance = 5
    const scrollLeft = mobileMenu.value.scrollLeft
    const maxScroll =
        mobileMenu.value.scrollWidth - mobileMenu.value.clientWidth

    showPrevButton.value = scrollLeft > tolerance
    showNextButton.value = scrollLeft < maxScroll - tolerance
}

onMounted(() => {
    setShow(true)

    const hash = route.hash
    if (hash) {
        const target = document.querySelector(hash) as HTMLElement
        if (target) {
            setTimeout(() => {
                target.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }, 200)
        }
    }

    window.screen.orientation.addEventListener(
        'change',
        handleOrientationChange
    )

    mobileMenu.value?.addEventListener('scroll', checkScrollButtons)
    window.addEventListener('resize', checkScrollButtons)

    const resizeObserver = new ResizeObserver(
        useDebounceFn(getUvisibleListElement, 500)
    )
    menuUlElement.value && resizeObserver.observe(menuUlElement.value)
    restartObserver()
})

onUnmounted(() => {
    window.screen.orientation.removeEventListener(
        'change',
        handleOrientationChange
    )
    mobileMenu.value?.removeEventListener('scroll', checkScrollButtons)
    window.removeEventListener('resize', checkScrollButtons)
})
</script>
<template>
  <nav
    ref="element"
    aria-label="Navigácia na stránke"
    class="page-header-menu-desktop d-none d-lg-flex justify-content-center z-index-15"
    data-cy="page_header_menu"
  >
    <div
      class="page-header-menu-desktop-inner d-flex align-items-center rounded-4 bg-secondary box-shadow-1 px-8 gap-8"
      :class="{
        'justify-content-center': !props.cta && !dropdownItems.length,
        'justify-content-between': props.cta || dropdownItems.length,
      }"
    >
      <ul
        ref="menuUlElement"
        class="d-inline-flex justify-content-start gap-8"
        :class="{
          'w-100': dropdownItems.length,
        }"
      >
        <template
          v-for="(link, index) in props.data"
          :key="`phmdl-${index}`"
        >
          <li v-if="link">
            <a
              v-if="typeof link.hash === 'string'"
              :href="link.hash"
              :aria-current="link.hash === activeHash ? 'true' : undefined"
              class="page-header-menu-desktop-link py-6 d-block text-hover-white transition position-relative text-decoration-none text-nowrap"
              :class="{
                'is-active text-white':
                  link.hash === activeHash,
              }"
            >
              {{ link.title }}
            </a>
            <VNuxtLink
              v-else
              :to="link.hash"
              :label="link.title"
              :aria-current="link.hash?.params?.id === route.params?.id ? 'true' : undefined"
              class="page-header-menu-desktop-link py-6 d-block text-hover-white transition position-relative text-decoration-none text-nowrap"
              :class="{
                'is-active text-white':
                  link.hash?.params?.id === route.params?.id,
              }"
            >
              {{ link.title }}
            </VNuxtLink>
          </li>
        </template>
      </ul>
      <div
        v-if="dropdownItems.length || props.cta"
        class="d-flex align-items-center gap-8"
      >
        <Dropdown
          v-if="dropdownItems.length"
          ref="dropdownRef"
          dropdown-id="page-header-more-menu"
          classes="small"
          class="z-3"
          @close="open = false"
        >
          <template #button>
            <button
              :aria-expanded="open"
              :aria-label="$t('Zobraziť ďalšie položky menu')"
              aria-haspopup="menu"
              aria-controls="page-header-more-menu-menu"
              class="page-header-dropdown rounded-full bg-transparent"
              @click.stop="open = !open"
            >
              <svg
                class="d-block w-100 h-100 transition text-white"
                aria-hidden="true"
              >
                <use xlink:href="#three-dots" />
              </svg>
            </button>
          </template>
          <template #content>
            <ul
              class="d-flex flex-column gap-2"
              role="none"
            >
              <template
                v-for="(link, index) in dropdownItems"
                :key="`phmdl-${index}`"
              >
                <li
                  v-if="link"
                  role="none"
                >
                  <a
                    v-if="typeof link.hash === 'string'"
                    :href="link.hash"
                    role="menuitem"
                    :class="{
                      'text-secondary':
                        link.hash === activeHash,
                      'text-black':
                        link.hash !== activeHash,
                    }"
                    @click.prevent="handleClick(link)"
                  >
                    {{ link.title }}
                  </a>
                  <VNuxtLink
                    v-else
                    :to="link.hash"
                    :label="link.title"
                    role="menuitem"
                    :class="{
                      'text-secondary':
                        link.hash?.params?.id ===
                        route.params?.id,
                      'text-black':
                        link.hash !== activeHash,
                    }"
                    @click="open = false"
                  >
                    {{ link.title }}
                  </VNuxtLink>
                </li>
              </template>
            </ul>
          </template>
        </Dropdown>
        <VButton
          v-if="props.cta"
          :target="props.cta.isExternal ? '_blank' : '_self'"
          :label="props.cta.text"
          color="white"
          size="small"
          :to="linkUrl"
          class="fw-normal text-nowrap flex-shrink-0"
        >
          {{ props.cta.text }}
        </VButton>
      </div>
    </div>
  </nav>
  <nav
    :aria-label="$t('Navigácia na stránke')"
    class="page-header-mobile-menu-button-wrapper d-lg-none"
    :class="{
      'gradient-left': !showPrevButton,
      'gradient-right': !showNextButton,
    }"
  >
    <div
      ref="mobileMenu"
      class="page-header-mobile-menu overflow-x-auto overflow-y-hidden d-flex align-items-center d-lg-none mb-12"
    >
      <template
        v-for="(link, index) in props.data"
        :key="`phm-${index}`"
      >
        <a
          v-if="typeof link.hash === 'string'"
          :id="`link-${link.hash}`"
          :href="link.hash"
          class="page-header-mobile-menu-item position-relative flex-shrink-0 px-2 py-4 text-decoration-none"
          :class="{
            'ps-4': index === 0,
            'pe-4': index === props.data.length - 1,
            'is-active':
              activeHash === link.hash ||
              link.hash === activeRoute,
          }"
          @click="handleClick(link)"
        >
          {{ link.title }}
        </a>
        <VNuxtLink
          v-else
          :to="link.hash"
          :label="link.title"
          class="2222 page-header-mobile-menu-item flex-shrink-0 px-2 text-decoration-none"
          :class="{
            'ps-4': index === 0,
            'pe-4': index === props.data.length - 1,
          }"
        >
          {{ link.title }}
        </VNuxtLink>
      </template>
    </div>
    <IconsWrapper>
      <IconsSymbolThreeDots />
    </IconsWrapper>
    <button
      v-if="showPrevButton"
      type="button"
      class="page-header-mobile-menu-button-prev"
      aria-label="Posunúť menu doprava"
      @click="handlePrev"
    >
      <svg
        class="transition flex-shrink-0"
        width="6"
        height="16"
        aria-hidden="true"
      >
        <use xlink:href="#angle-left" />
      </svg>
    </button>
    <button
      v-if="showNextButton"
      type="button"
      class="page-header-mobile-menu-button-next"
      aria-label="Posunúť menu doľava"
      @click="handleNext"
    >
      <svg
        class="transition flex-shrink-0"
        width="6"
        height="16"
        aria-hidden="true"
      >
        <use xlink:href="#angle-right" />
      </svg>
    </button>
  </nav>
</template>
<style lang="scss">
@import "./PageHeaderMenu.vars.scss";
</style>
<style lang="scss" scoped>
@import "./PageHeaderMenu.scss";
</style>
